import CustomReportChannel from '@/Enums/ReportBuilder/ReportChannel.enum';
import type { IPaginatedApiResponse } from '@/Interfaces/ApiResponse/PaginatedApiResponse.interface';
import type { IClientControlOption, IControlOption, IGroupedControlOption } from '@/Interfaces/ControlOption.interface';
import type { ICustomReport } from '@/Interfaces/ReportBuilder/CustomReport.interface';
import type { ICustomReportListRequestPayload } from '@/Interfaces/ReportBuilder/RequestPayload.interface';
import type { IScheduledCustomReport } from '@/Interfaces/ReportBuilder/ScheduledCustomReport.interface';
import type { ICustomReportTemplate, ICustomReportTemplateOption } from '@/Interfaces/ReportBuilder/Template.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { beeswaxEventsToControlOptions } from '@/helpers/beeswaxEvents.helpers';
import { getSortFilter } from '@/helpers/grid/getSortFilter';
import {
  CAMPAIGN_OPTIONS_FETCH_MAP,
  fetchBeeswaxEvents,
  fetchInstantReports,
  fetchScheduledReports,
  fetchTemplate,
  fetchTemplates,
} from '@/store/api/reportBuilder.api';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import type { OmniplatformChannel } from '@/types/ReportBuilder/Omniplatform.type';
import type { RequestId } from '@/types/Slice.type';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

interface IState {
  generatedReports: ICustomReport[];
  generatedReportsTotal: number;
  generatedReportsRequestId: RequestId;
  scheduledReports: IScheduledCustomReport[];
  scheduledReportsTotal: number;
  scheduledReportsRequestId: RequestId;
  campaigns: IControlOption[];
  campaignsRequestId: RequestId;
  events: IControlOption[];
  eventsRequestId: RequestId;
  templates: ICustomReportTemplateOption[];
  templatesRequestId: RequestId;
  templateLoadRequestId: RequestId;
}

enum Action {
  GetScheduledReports = 'omniplatformReportBuilderSlice/getScheduledReports',
  GetTemplates = 'omniplatformReportBuilderSlice/getTemplates',
  GetGeneratedReports = 'omniplatformReportBuilderSlice/getGeneratedReports',
  GetCampaigns = 'omniplatformReportBuilderSlice/getCampaigns',
  GetEvents = 'omniplatformReportBuilderSlice/getEvents',
  LoadTemplate = 'omniplatformReportBuilderSlice/loadTemplate',
}

const initialState: IState = {
  generatedReports: [],
  generatedReportsTotal: 0,
  generatedReportsRequestId: DEFAULT_LOADING_REQUEST_ID,
  scheduledReports: [],
  scheduledReportsTotal: 0,
  scheduledReportsRequestId: DEFAULT_LOADING_REQUEST_ID,
  campaigns: [],
  campaignsRequestId: DEFAULT_LOADING_REQUEST_ID,
  events: [],
  eventsRequestId: DEFAULT_LOADING_REQUEST_ID,
  templates: [],
  templatesRequestId: DEFAULT_LOADING_REQUEST_ID,
  templateLoadRequestId: null,
};

const CUSTOM_REPORT_CHANNEL = CustomReportChannel.Omniplatform;

export const omniplatformReportBuilderSlice = createSlice({
  name: 'omniplatformReportBuilder',
  initialState,
  reducers: {
    appendTemplate: (state, action) => {
      state.templates = [action.payload, ...state.templates];
    },
    resetOmniplatformReportBuilderState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getCampaigns.pending, pendingCaseHandler('campaigns'))
      .addCase(getCampaigns.rejected, rejectedCaseHandler('campaigns'))
      .addCase(getCampaigns.fulfilled, fulfilledCaseHandler('campaigns'))
      .addCase(getEvents.pending, pendingCaseHandler('events'))
      .addCase(getEvents.rejected, rejectedCaseHandler('events'))
      .addCase(getEvents.fulfilled, fulfilledCaseHandler('events'))
      .addCase(getTemplates.pending, pendingCaseHandler('templates'))
      .addCase(getTemplates.rejected, rejectedCaseHandler('templates'))
      .addCase(getTemplates.fulfilled, fulfilledCaseHandler('templates'))
      .addCase(loadTemplate.pending, pendingCaseHandler('templateLoad'))
      .addCase(loadTemplate.rejected, rejectedCaseHandler('templateLoad'))
      .addCase(loadTemplate.fulfilled, (state, action) => {
        if (state.templateLoadRequestId === action.meta.requestId) {
          state.templateLoadRequestId = null;
        }
      })
      .addCase(getGeneratedReports.pending, pendingCaseHandler('generatedReports'))
      .addCase(getGeneratedReports.rejected, rejectedCaseHandler('generatedReports'))
      .addCase(getGeneratedReports.fulfilled, (state, action) => {
        if (state.generatedReportsRequestId === action.meta.requestId) {
          state.generatedReportsRequestId = null;
          state.generatedReports = action.payload?.data ?? [];
          state.generatedReportsTotal = action.payload?.meta?.total ?? 0;
        }
      })
      .addCase(getScheduledReports.pending, pendingCaseHandler('scheduledReports'))
      .addCase(getScheduledReports.rejected, rejectedCaseHandler('scheduledReports'))
      .addCase(getScheduledReports.fulfilled, (state, action) => {
        if (state.scheduledReportsRequestId === action.meta.requestId) {
          state.scheduledReportsRequestId = null;
          state.scheduledReports = action.payload?.data ?? [];
          state.scheduledReportsTotal = action.payload?.meta?.total ?? 0;
        }
      });
  },
});

export const { appendTemplate, resetOmniplatformReportBuilderState } = omniplatformReportBuilderSlice.actions;

export const getScheduledReports = createAsyncThunk<
  IPaginatedApiResponse<IScheduledCustomReport[]>,
  Partial<ICustomReportListRequestPayload> & IAbortSignalThunkArg
>(Action.GetScheduledReports, async payload => {
  const { pageSize, page, sort, signal } = payload ?? {};
  const params = {
    limit: pageSize ?? 15,
    page: page ?? 1,
    sort: getSortFilter(sort),
  };

  return await fetchScheduledReports(params, signal, CUSTOM_REPORT_CHANNEL);
});

export const getGeneratedReports = createAsyncThunk<
  IPaginatedApiResponse<ICustomReport[]>,
  Partial<ICustomReportListRequestPayload> & IAbortSignalThunkArg
>(Action.GetGeneratedReports, async payload => {
  const { pageSize, page, sort, signal } = payload ?? {};
  const params = {
    limit: pageSize ?? 15,
    page: page ?? 1,
    sort: getSortFilter(sort),
  };

  return await fetchInstantReports(params, signal, CUSTOM_REPORT_CHANNEL);
});

export const getCampaigns = createAsyncThunk<
  IGroupedControlOption[],
  IAbortSignalThunkArg & {
    advertisers: IControlOption[];
    clients: IClientControlOption[];
    platforms?: IControlOption<string, OmniplatformChannel>[];
  }
>(Action.GetCampaigns, async payload => {
  const { advertisers, clients, platforms, signal } = payload;
  if (!platforms) {
    return [];
  }
  const advertiserIds: number[] = [];

  if (!advertisers?.length) {
    clients.forEach(client => {
      client.advertisers?.forEach(advertiser => advertiserIds.push(advertiser.value));
    });
  } else {
    advertisers.forEach(advertiser => {
      advertiserIds.push(advertiser.value);
    });
  }

  if (!advertiserIds?.length) {
    return [];
  }

  const groupedOptions = await Promise.all(
    platforms.map(async platform => {
      const fetcher = CAMPAIGN_OPTIONS_FETCH_MAP[platform.value];
      if (!fetcher) {
        return [];
      }
      return await fetcher(advertiserIds, signal);
    }),
  );

  return groupedOptions.flat();
});

export const getEvents = createAsyncThunk<
  IControlOption[],
  IAbortSignalThunkArg & {
    advertisers: IControlOption[];
    clients: IClientControlOption[];
    platforms?: IControlOption<string, OmniplatformChannel>[];
  }
>(Action.GetEvents, async payload => {
  const { advertisers, clients, platforms, signal } = payload;
  if (!platforms?.length || platforms.some(platform => platform.value !== CustomReportChannel.Beeswax)) {
    return [];
  }
  const advertiserIds: number[] = [];

  if (!advertisers?.length) {
    clients.forEach(client => {
      client.advertisers?.forEach(advertiser => advertiserIds.push(advertiser.value));
    });
  } else {
    advertisers.forEach(advertiser => {
      advertiserIds.push(advertiser.value);
    });
  }

  const events = await fetchBeeswaxEvents(advertiserIds, signal);

  return beeswaxEventsToControlOptions(events);
});

export const getTemplates = createAsyncThunk<ICustomReportTemplateOption[], IAbortSignalThunkArg | undefined>(
  Action.GetTemplates,
  async payload => {
    return await fetchTemplates(payload?.signal, CUSTOM_REPORT_CHANNEL);
  },
);

export const loadTemplate = createAsyncThunk<
  ICustomReportTemplate,
  Partial<IAbortSignalThunkArg> & { id: ICustomReportTemplate['id'] }
>(Action.LoadTemplate, async payload => {
  const { signal, id } = payload ?? {};
  return await fetchTemplate(id, signal, CUSTOM_REPORT_CHANNEL);
});

export default omniplatformReportBuilderSlice.reducer;
